import { useState } from 'react';

import { Icon, IconButton, Modal, Progress } from '@atom/mui';
import { ArrowBackIcon, DoneAllIcon } from '@atom/mui/icons';
import { AssetElement } from '@atom/types/asset';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import classes from './workOrderAssetView.module.css';

type WorkOrderAssetHeaderProps = {
  root: AssetElement | undefined;
  saving: boolean;
  hasUnsavedChanges: boolean;
  previewView: boolean;
  onBack: () => void;
  onApproveAll: () => Promise<void>;
};

const WorkOrderAssetHeader = ({
  root,
  saving,
  hasUnsavedChanges,
  previewView,
  onBack,
  onApproveAll,
}: WorkOrderAssetHeaderProps) => {
  const [approveOpen, setApproveOpen] = useState<boolean>(false);

  const canApproveAll =
    hasRolePermissions(ROLE_SETS.MANAGER) && Boolean(root?.changeType);

  const onConfirmApproveAll = async () => {
    setApproveOpen(false);
    await onApproveAll();
  };

  const status = saving ? (
    <span className={classes.saving}>
      <Progress size={16} thickness={4} />
      Saving...
    </span>
  ) : hasUnsavedChanges ? (
    <span className={classes.unsavedChanges}>Unsaved Changes.</span>
  ) : (
    'All changes saved.'
  );

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.backButtonContainer}>
          <IconButton onClick={onBack} data-cy="workOrderAssetBackButton">
            <Icon icon={ArrowBackIcon} />
          </IconButton>
          <div>Back to Tasks</div>
        </div>
        {!previewView && (
          <div
            className={classes.changesContainer}
            data-cy="workOrderAssetSaveStatus"
          >
            {status}
            {canApproveAll && (
              <IconButton
                tooltip="Approve All Changes"
                onClick={() => setApproveOpen(true)}
                disabled={hasUnsavedChanges}
                data-cy="workOrderAssetApproveAllButton"
              >
                <Icon icon={DoneAllIcon} />
              </IconButton>
            )}
          </div>
        )}
      </div>
      <Modal
        open={approveOpen}
        onCancel={() => setApproveOpen(false)}
        confirmButtonText="Approve All"
        onConfirm={onConfirmApproveAll}
        title={`Approve All Changes for ${root?.name ?? ''}`}
        ConfirmButtonProps={{ 'data-cy': 'workOrderAssetApproveAllConfirm' }}
        CancelButtonProps={{ 'data-cy': 'workOrderAssetApproveAllCancel' }}
      >
        <span>
          Are you sure you want to approve all changes to
          <strong> {root?.name} </strong>in all tasks?
        </span>
      </Modal>
    </>
  );
};

export default WorkOrderAssetHeader;
